/**
 * File System Access API with download/upload fallbacks.
 */

import { showToast } from '../state/toast.svelte'

export async function openMarkdownFile(): Promise<string | null> {
  if (typeof window.showOpenFilePicker === 'function') {
    try {
      const [handle] = await window.showOpenFilePicker({
        multiple: false,
        types: [
          {
            description: 'Markdown',
            accept: { 'text/markdown': ['.md', '.markdown', '.txt'] },
          },
        ],
      })
      if (!handle) return null
      const file = await handle.getFile()
      const text = await file.text()
      showToast(`Opened ${file.name}`)
      return text
    } catch (err) {
      if ((err as DOMException).name === 'AbortError') return null
      showToast('Could not open file')
      return null
    }
  }

  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.md,.markdown,.txt,text/markdown,text/plain'
    input.onchange = () => {
      const file = input.files?.[0]
      if (!file) {
        resolve(null)
        return
      }
      void file.text().then((text) => {
        showToast(`Opened ${file.name}`)
        resolve(text)
      })
    }
    input.click()
  })
}

export async function saveMarkdownFile(source: string, suggestedName: string): Promise<void> {
  const name = suggestedName.endsWith('.md') ? suggestedName : `${suggestedName}.md`

  if (typeof window.showSaveFilePicker === 'function') {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: name,
        types: [{ description: 'Markdown', accept: { 'text/markdown': ['.md'] } }],
      })
      const writable = await handle.createWritable()
      await writable.write(source)
      await writable.close()
      showToast('Saved')
      return
    } catch (err) {
      if ((err as DOMException).name === 'AbortError') return
    }
  }

  const blob = new Blob([source], { type: 'text/markdown' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 0)
  showToast('Downloaded')
}
